import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, from, fromEvent, interval, merge, of, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  switchMap,
  tap,
  throttleTime,
  withLatestFrom,
} from 'rxjs/operators';
import {
  HARD_NAVIGATOR,
  LogoutReason,
  SESSION_LOGOUT_HANDLER,
} from '../../app.tokens';
import { SessionTimeoutDialogComponent } from '../../common/components/session-timeout-dialog/session-timeout-dialog.component';
import { SessionActivityService } from '../../common/services/session-activity/session-activity.service';
import * as AppLifecyclesActions from './app-lifecycles.actions';
import { sameTickSchedule } from './app-lifecycles.helpers';
import {
  selectIsSessionExpired,
  selectIsSessionTracking,
  selectIsWarningActive,
  selectTickSchedule,
} from './app-lifecycles.selectors';
import { SESSION_TIMEOUT_CONFIG } from './session-timeout.config';

/** Display resolution of the countdown, not a configurable threshold. */
const COUNTDOWN_TICK_MS = 1_000;

/** Matches the reconciliation throttle used by jobs-status.effects.ts. */
const WAKE_THROTTLE_MS = 2_000;

/** Where a terminated session lands. Matches the post-logout hook's target. */
const LOGIN_PATH = '/welcome';

/**
 * EDUS-2655 — drives the inactivity timeout.
 *
 * The store owns the decisions; these effects only supply it with the two
 * things a reducer cannot produce for itself: when the user did something,
 * and what time it is now.
 */
@Injectable()
export class SessionTimeoutEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly activity = inject(SessionActivityService);
  private readonly config = inject(SESSION_TIMEOUT_CONFIG);
  private readonly dialog = inject(MatDialog);
  private readonly logoutHandler = inject(SESSION_LOGOUT_HANDLER);
  private readonly navigator = inject(HARD_NAVIGATOR);

  /**
   * Starts on the same signal the rest of the app treats as "logged in", so a
   * page refresh into an existing Firebase session starts tracking too.
   *
   * Ignored when already tracking. `authCompleted` is not once-per-login:
   * Firebase refreshes the ID token roughly hourly, `onIdTokenChanged` fires,
   * and UserIdentityService re-validates and dispatches `setUser` again, which
   * maps to another `authCompleted`. Restarting the clock there would let a
   * background token refresh silently extend an idle session — precisely the
   * "background processes must not count as activity" rule in FR-1.
   */
  startTracking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppLifecyclesActions.authCompleted),
      withLatestFrom(this.store.select(selectIsSessionTracking)),
      filter(([, alreadyTracking]) => !alreadyTracking),
      map(() =>
        AppLifecyclesActions.sessionTrackingStarted({
          atMs: Date.now(),
          config: this.config,
        })
      )
    )
  );

  stopTracking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppLifecyclesActions.logoutCompleted),
      tap(() => this.activity.clearPeerState()),
      map(() => AppLifecyclesActions.sessionTrackingStopped())
    )
  );

  /** DOM listeners exist only while a session is being tracked. */
  trackActivity$ = createEffect(() =>
    this.store.select(selectIsSessionTracking).pipe(
      switchMap((tracking) => (tracking ? this.activity.userActivity$ : EMPTY)),
      map((atMs) => AppLifecyclesActions.activityDetected({ atMs }))
    )
  );

  /**
   * The only timer in the feature. While idle it is a single `timer` that
   * wakes at the warning threshold — no polling — and it steps up to 1Hz only
   * for the 60 seconds the countdown is on screen.
   */
  scheduleTick$ = createEffect(() =>
    this.store.select(selectTickSchedule).pipe(
      distinctUntilChanged(sameTickSchedule),
      switchMap((schedule) => {
        switch (schedule.kind) {
          case 'stopped':
            return EMPTY;
          case 'countdown':
            return interval(COUNTDOWN_TICK_MS);
          case 'idle':
            return timer(Math.max(0, schedule.warnsAt - Date.now()));
        }
      }),
      map(() => AppLifecyclesActions.sessionTick({ nowMs: Date.now() }))
    )
  );

  /**
   * AC 1.5. Browsers throttle background timers and stop them entirely while
   * the machine sleeps, so the scheduled tick above cannot be trusted to have
   * fired on time. Re-reading the clock whenever the tab comes back is enough
   * to correct it, because warning and expiry are derived from timestamps
   * rather than counted down.
   */
  reconcileOnWake$ = createEffect(() =>
    merge(
      fromEvent(document, 'visibilitychange').pipe(
        filter(() => !document.hidden)
      ),
      fromEvent(window, 'focus'),
      fromEvent(window, 'online')
    ).pipe(
      throttleTime(WAKE_THROTTLE_MS),
      withLatestFrom(this.store.select(selectIsSessionTracking)),
      filter(([, tracking]) => tracking),
      switchMap(() => {
        // A frozen tab processes no `storage` events at all, so a peer that
        // terminated the session while this tab slept is otherwise invisible
        // to it — `terminateFromPeer$` never fires because the broadcast was
        // missed, and this tab would just tick forward on its own stale clock
        // as if nothing happened (AC 3.2). Check for that directly before
        // falling back to activity reconciliation.
        const peerEvent = this.activity.readPeerEvent();
        if (peerEvent?.type === 'terminated') {
          return of(AppLifecyclesActions.peerSessionTerminated());
        }

        // A frozen tab processed no `storage` events, so it can wake believing
        // it sat idle for however long it was asleep while another tab was in
        // active use. Re-read the shared timestamp before advancing the clock.
        //
        // Order matters: the peer timestamp has to land BEFORE the tick. Tick
        // first and this tab could momentarily compute "expired" off its stale
        // clock, which is enough for expire$ to fire and log the user out.
        const peerActivityAt = this.activity.readPeerActivity();
        const tick = AppLifecyclesActions.sessionTick({ nowMs: Date.now() });
        return peerActivityAt === null
          ? of(tick)
          : of(
              AppLifecyclesActions.activityDetected({ atMs: peerActivityAt }),
              tick
            );
      })
    )
  );

  /**
   * Announces expiry exactly once per session. What happens next — audit
   * entry, credential wipe, redirect — is EDUS-2657.
   */
  expire$ = createEffect(() =>
    this.store.select(selectIsSessionExpired).pipe(
      distinctUntilChanged(),
      filter((expired) => expired),
      map(() => AppLifecyclesActions.sessionExpired())
    )
  );

  /**
   * The modal is a projection of store state, opened and closed here rather
   * than by the component. That is what makes AC 1.4 hold: `disableClose`
   * blocks Esc and backdrop clicks, and because the countdown and the
   * termination both live in the store, destroying the element in devtools
   * cannot stop the session from expiring.
   */
  openWarningModal$ = createEffect(
    () =>
      this.store.select(selectIsWarningActive).pipe(
        distinctUntilChanged(),
        tap((warningActive) => {
          if (warningActive) {
            this.openWarningDialog();
          } else {
            this.closeWarningDialog();
          }
        })
      ),
    { dispatch: false }
  );

  private warningDialogRef: MatDialogRef<SessionTimeoutDialogComponent> | null =
    null;

  private openWarningDialog(): void {
    if (this.warningDialogRef) {
      return;
    }
    this.warningDialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
      disableClose: true,
      autoFocus: 'dialog',
    });
    this.warningDialogRef.afterClosed().subscribe(() => {
      this.warningDialogRef = null;
    });
  }

  private closeWarningDialog(): void {
    this.warningDialogRef?.close();
    this.warningDialogRef = null;
  }

  /**
   * AC 2.1 / AC 2.4. Signs the user out, then leaves the page by document
   * navigation rather than router navigation — see HARD_NAVIGATOR for why
   * that distinction is load-bearing rather than stylistic.
   *
   * `exhaustMap` so a duplicated signal cannot start two logouts, and the
   * redirect runs even when the logout itself fails: a user left staring at
   * authenticated data because an audit write timed out is the exact outcome
   * this feature exists to prevent.
   */
  terminate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AppLifecyclesActions.sessionExpired,
          AppLifecyclesActions.sessionLogoutRequested
        ),
        map(
          (action): LogoutReason =>
            action.type === AppLifecyclesActions.sessionExpired.type
              ? 'inactivity-timeout'
              : 'user-initiated'
        ),
        exhaustMap((reason) =>
          from(this.logoutHandler.logout(reason)).pipe(
            catchError(() => of(undefined))
          )
        ),
        tap(() => this.navigator.assign(LOGIN_PATH))
      ),
    { dispatch: false }
  );

  /**
   * AC 2.2. A document navigation can leave the authenticated page in the
   * back/forward cache, and restoring from it replays the rendered DOM without
   * re-running bootstrap or the route guard. Reloading on a persisted restore
   * forces the guard to run and bounce to the login page.
   *
   * Only when no session is being tracked — returning from an external site
   * mid-session is an ordinary restore and must not reload the app.
   */
  reloadOnRestoreFromBfCache$ = createEffect(
    () =>
      fromEvent<PageTransitionEvent>(window, 'pageshow').pipe(
        filter((event) => event.persisted),
        withLatestFrom(this.store.select(selectIsSessionTracking)),
        filter(([, tracking]) => !tracking),
        tap(() => this.navigator.reload())
      ),
    { dispatch: false }
  );

  /**
   * Applies what peer tabs report (AC 3.1 / 3.2 / 3.3).
   *
   * Peer activity arrives as the ordinary `activityDetected`, so the reducer
   * applies exactly the same rules to it as to local input — including AC
   * 3.4, where activity stops extending the session once the countdown shows.
   */
  syncPeerEvents$ = createEffect(() =>
    this.store.select(selectIsSessionTracking).pipe(
      switchMap((tracking) => (tracking ? this.activity.peerEvents$ : EMPTY)),
      map((event) => {
        switch (event.type) {
          case 'activity':
            return AppLifecyclesActions.activityDetected({ atMs: event.atMs });
          case 'extended':
            return AppLifecyclesActions.peerSessionExtended({
              atMs: event.atMs,
            });
          case 'terminated':
            return AppLifecyclesActions.peerSessionTerminated();
        }
      })
    )
  );

  /**
   * Broadcasts this tab's decisions. Peer-sourced equivalents are deliberately
   * absent from the `ofType` list — rebroadcasting them would have two tabs
   * echoing the same event indefinitely.
   */
  publishToPeers$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AppLifecyclesActions.sessionExtended,
          AppLifecyclesActions.sessionExpired,
          AppLifecyclesActions.sessionLogoutRequested
        ),
        tap((action) =>
          this.activity.publish(
            action.type === AppLifecyclesActions.sessionExtended.type
              ? 'extended'
              : 'terminated'
          )
        )
      ),
    { dispatch: false }
  );

  /**
   * AC 3.2 — the gap this epic actually had to close. Firebase already
   * propagates the sign-out itself, but nothing acted on it, so a background
   * tab kept rendering authenticated data until its next route change.
   *
   * No logout and no audit entry here: the tab that ended the session already
   * wrote both. This one just leaves.
   */
  terminateFromPeer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppLifecyclesActions.peerSessionTerminated),
        tap(() => this.navigator.assign(LOGIN_PATH))
      ),
    { dispatch: false }
  );
}
